/**
 * StoreMenu — orchestrator for the live menu page.
 *
 * Responsibilities:
 *  - Read filter/sort/paging state from URL params.
 *  - Two-phase fetch: PHASE 1 = 24-row paged paint; PHASE 2 = full 2000-row
 *    pool for accurate sidebar facets + client-side filter pipeline.
 *  - Compute dynamic facet options (brand / weight / potency / strain) where
 *    each axis is computed excluding its own filter so options are mutually
 *    aware.
 *  - Hand presentation off to MenuHeader / MenuFilters / MenuGrid.
 */
import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { X } from "lucide-react";

import SEOHead from "../components/SEOHead";
import { getMenu, getLocation, getCategories, getSpecials } from "../lib/api";
import { classifySpecial } from "../lib/promos";

import MenuHeader, { SORT_OPTIONS } from "./menu/MenuHeader";
import MenuFilters, { STRAINS, WEIGHTS, POTENCY } from "./menu/MenuFilters";
import MenuGrid from "./menu/MenuGrid";

const PAGE_SIZE = 24;

export default function StoreMenu() {
    const { storeSlug } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const category = searchParams.get("category") || "";
    const subcategory = searchParams.get("subcategory") || "";
    const search = searchParams.get("q") || "";
    // Multi-select for ALL filter axes. URL stores comma-separated values:
    // ?strain=INDICA,SATIVA&weight=1g,3.5g&potency=20-30,30-100&brand=A,B,C
    const arrFrom = (p) => {
        const v = searchParams.get(p) || "";
        return v ? v.split(",").filter(Boolean) : [];
    };
    const selectedStrains = useMemo(() => arrFrom("strain"), [searchParams]);
    const selectedBrands = useMemo(() => arrFrom("brand"), [searchParams]);
    const selectedWeights = useMemo(() => arrFrom("weight"), [searchParams]);
    const selectedPotency = useMemo(() => arrFrom("potency"), [searchParams]);
    const sortKey = searchParams.get("sort_key") || "POPULAR";
    const sortDir = searchParams.get("sort_dir") || "DESC";
    const offsetParam = parseInt(searchParams.get("offset") || "0", 10);

    const [menu, setMenu] = useState(null);
    const [pool, setPool] = useState(null);
    const [store, setStore] = useState(null);
    const [categories, setCategories] = useState([]);
    const [specials, setSpecials] = useState([]);
    const [error, setError] = useState(null);
    const [searchInput, setSearchInput] = useState(search);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

    const needsClientFilter = !!(
        subcategory ||
        selectedStrains.length ||
        selectedBrands.length ||
        selectedWeights.length ||
        selectedPotency.length
    );

    // Live search — debounce the in-flight input and push to the URL after
    // 250ms of inactivity. We read from `window.location.search` (not the
    // closure-captured `searchParams`) so a concurrent clearAll() that
    // cleared the URL right before our timer fires can't be silently
    // re-overwritten with stale-closure params. React Router v7's
    // setSearchParams callback form gets a stale `prev` and is unsafe here.
    // The timer ref also lets clearAll() cancel a pending debounce.
    const searchTimerRef = useRef(null);
    useEffect(() => {
        if (searchInput === search) return;
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            const sp = new URLSearchParams(window.location.search);
            const v = searchInput.trim();
            if (v) sp.set("q", v);
            else sp.delete("q");
            sp.delete("offset");
            setSearchParams(sp, { replace: true });
            searchTimerRef.current = null;
        }, 250);
        return () => {
            if (searchTimerRef.current) {
                clearTimeout(searchTimerRef.current);
                searchTimerRef.current = null;
            }
        };
    }, [searchInput]);

    // PHASE 1 — fast first paint (24 rows from the API at the right offset).
    useEffect(() => {
        let cancelled = false;
        getMenu(storeSlug, {
            limit: PAGE_SIZE,
            offset: offsetParam,
            category: category || undefined,
            strain_type:
                selectedStrains.length === 1 ? selectedStrains[0] : undefined,
            search: search || undefined,
            sort_key: sortKey,
            sort_direction: sortDir,
        })
            .then((d) => !cancelled && setMenu(d))
            .catch(() => !cancelled && setError("error"));
        return () => {
            cancelled = true;
        };
    }, [storeSlug, category, selectedStrains, search, sortKey, sortDir, offsetParam]);

    // PHASE 2 — full eligible pool (up to ~2000 rows) for facet counts, the
    // filtered-grid view, AND the live search dropdown's client-side
    // type-ahead. We deliberately DON'T pass strain to the server when
    // multiple are selected — the client-side facet pipeline handles that.
    useEffect(() => {
        let cancelled = false;
        Promise.resolve().then(() => {
            if (!cancelled) setPool(null);
        });
        getMenu(storeSlug, {
            limit: 2000,
            offset: 0,
            category: category || undefined,
            sort_key: sortKey,
            sort_direction: sortDir,
        })
            .then((d) => {
                if (!cancelled) setPool(d);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [storeSlug, category, sortKey, sortDir]);

    useEffect(() => {
        let cancelled = false;
        getLocation(storeSlug).then((d) => !cancelled && setStore(d)).catch(() => {});
        getCategories().then((d) => !cancelled && setCategories(d)).catch(() => {});
        getSpecials(storeSlug)
            .then((d) => !cancelled && setSpecials(d?.specials || []))
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [storeSlug]);

    // ---- URL helpers — generic multi-select toggle ----------------------
    const setParam = (key, value) => {
        const sp = new URLSearchParams(searchParams);
        if (value === undefined || value === null || value === "" || value === false) sp.delete(key);
        else sp.set(key, value);
        sp.delete("offset");
        setSearchParams(sp);
    };
    const toggleArrayParam = (key, value, currentArr) => {
        const set = new Set(currentArr);
        if (set.has(value)) set.delete(value);
        else set.add(value);
        const next = Array.from(set);
        setParam(key, next.length ? next.join(",") : null);
    };
    const toggleStrain = (v) => toggleArrayParam("strain", v, selectedStrains);
    const toggleBrand = (v) => toggleArrayParam("brand", v, selectedBrands);
    const toggleWeight = (v) => toggleArrayParam("weight", v, selectedWeights);
    const togglePotency = (v) => toggleArrayParam("potency", v, selectedPotency);
    const clearBrands = () => setParam("brand", null);
    const setOffset = (o) => {
        const sp = new URLSearchParams(searchParams);
        if (o > 0) sp.set("offset", o);
        else sp.delete("offset");
        setSearchParams(sp);
    };
    const clearAll = () => {
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
            searchTimerRef.current = null;
        }
        setSearchParams(new URLSearchParams(), { replace: true });
        setSearchInput("");
        setMobileFiltersOpen(false);
    };
    const onSortChange = (value) => {
        const [k, d] = value.split(":");
        const sp = new URLSearchParams(searchParams);
        sp.set("sort_key", k);
        sp.set("sort_dir", d);
        sp.delete("offset");
        setSearchParams(sp);
    };

    const sortValue = `${sortKey}:${sortDir}`;
    const activeFilterCount = useMemo(
        () =>
            (category ? 1 : 0) +
            (subcategory ? 1 : 0) +
            (search ? 1 : 0) +
            selectedStrains.length +
            selectedBrands.length +
            selectedWeights.length +
            selectedPotency.length,
        [category, subcategory, search, selectedStrains, selectedBrands, selectedWeights, selectedPotency]
    );

    const canonical = `https://weeddispensarynearme.com/menu/${storeSlug}`;
    const classifiedSpecials = useMemo(
        () => specials.map(classifySpecial).filter(Boolean),
        [specials]
    );
    const specialsById = useMemo(() => {
        const idx = {};
        classifiedSpecials.forEach((c) => {
            idx[c.id] = c;
        });
        return idx;
    }, [classifiedSpecials]);

    // ---- Facet computation pipeline --------------------------------------
    // Per-product matchers, kept pure so they can be re-applied for each
    // filter-axis's option list (excluding that axis). This produces
    // dynamic, mutually-aware filter options. ALL axes accept arrays (OR
    // semantics within an axis, AND across axes).
    const matchStrain = (p, list) =>
        !list?.length || list.includes(p.strainType);
    const matchSubcategory = (p, sc) => !sc || p.subcategory === sc;
    const matchBrands = (p, list) =>
        !list?.length || list.includes(p?.brand?.name || "");
    const matchWeights = (p, list) => {
        if (!list?.length) return true;
        const wantSet = new Set(list.map((w) => w.toLowerCase()));
        return (p.variants || []).some((v) =>
            wantSet.has((v.option || "").toLowerCase())
        );
    };
    const matchPotency = (p, bands) => {
        if (!bands?.length) return true;
        const range = p?.potencyThc?.range || [];
        const upper = Number(range[1] ?? range[0] ?? 0);
        return bands.some((band) => {
            const [lo, hi] = band.split("-").map(Number);
            return upper > lo && upper <= hi;
        });
    };
    const applyFilters = (input, exclude = null) =>
        (input || []).filter((p) => {
            if (exclude !== "strain" && !matchStrain(p, selectedStrains)) return false;
            if (exclude !== "subcategory" && !matchSubcategory(p, subcategory))
                return false;
            if (exclude !== "brand" && !matchBrands(p, selectedBrands))
                return false;
            if (exclude !== "weight" && !matchWeights(p, selectedWeights)) return false;
            if (exclude !== "potency" && !matchPotency(p, selectedPotency))
                return false;
            return true;
        });

    const facetPool = pool?.products || menu?.products || [];

    const brandOptions = useMemo(() => {
        const filtered = applyFilters(facetPool, "brand");
        const counts = {};
        for (const p of filtered) {
            const name = p?.brand?.name;
            if (!name) continue;
            counts[name] = (counts[name] || 0) + 1;
        }
        return Object.entries(counts)
            .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
            .map(([name, count]) => ({ name, count }));
    }, [facetPool, selectedStrains, subcategory, selectedWeights, selectedPotency]);

    const weightOptions = useMemo(() => {
        const filtered = applyFilters(facetPool, "weight");
        const present = new Set();
        for (const p of filtered) {
            for (const v of p.variants || []) {
                const opt = (v.option || "").trim();
                if (opt) present.add(opt.toLowerCase());
            }
        }
        return WEIGHTS.filter((w) => present.has(w.val.toLowerCase()));
    }, [facetPool, selectedStrains, subcategory, selectedBrands, selectedPotency]);

    const potencyOptions = useMemo(() => {
        const filtered = applyFilters(facetPool, "potency");
        return POTENCY.filter((band) => {
            const [lo, hi] = band.val.split("-").map(Number);
            return filtered.some((p) => {
                const range = p?.potencyThc?.range || [];
                const upper = Number(range[1] ?? range[0] ?? 0);
                return upper > lo && upper <= hi;
            });
        });
    }, [facetPool, selectedStrains, subcategory, selectedBrands, selectedWeights]);

    const strainOptions = useMemo(() => {
        const filtered = applyFilters(facetPool, "strain");
        const present = new Set(filtered.map((p) => p.strainType).filter(Boolean));
        return STRAINS.filter((s) => present.has(s.val));
    }, [facetPool, subcategory, selectedBrands, selectedWeights, selectedPotency]);

    const subcategories = useMemo(
        () =>
            Array.from(
                new Set(
                    (menu?.products || [])
                        .map((p) => p.subcategory)
                        .filter(Boolean)
                )
            ).sort(),
        [menu]
    );

    // ---- Visible grid + pagination math -----------------------------------
    const clientFiltered = useMemo(() => {
        if (!needsClientFilter) return menu?.products || [];
        return applyFilters(facetPool);
    }, [facetPool, menu, needsClientFilter, subcategory, selectedBrands, selectedWeights, selectedPotency, selectedStrains]);

    const visibleCount = needsClientFilter
        ? clientFiltered.length
        : menu?.productsCount || 0;
    const pageStart = needsClientFilter ? offsetParam : 0;
    const pageEnd = needsClientFilter
        ? offsetParam + PAGE_SIZE
        : clientFiltered.length;
    const visibleProducts = needsClientFilter
        ? clientFiltered.slice(pageStart, pageEnd)
        : menu?.products || [];
    const hasNext = needsClientFilter
        ? offsetParam + PAGE_SIZE < clientFiltered.length
        : (menu?.products?.length || 0) > 0 &&
          offsetParam + (menu?.products?.length || 0) < (menu?.productsCount || 0);
    const hasPrev = offsetParam > 0;

    const onSearchCommit = (v) => {
        const sp = new URLSearchParams(searchParams);
        if (v) sp.set("q", v);
        else sp.delete("q");
        sp.delete("offset");
        setSearchParams(sp);
    };

    const filterPanelProps = {
        selectedStrains,
        selectedBrands,
        selectedWeights,
        selectedPotency,
        brandOptions,
        weightOptions,
        potencyOptions,
        strainOptions,
        onToggleStrain: toggleStrain,
        onToggleBrand: toggleBrand,
        onToggleWeight: toggleWeight,
        onTogglePotency: togglePotency,
        onClearBrands: clearBrands,
    };

    return (
        <>
            <SEOHead
                title={`Live Menu — ${store?.short_name || "ZenZest"} Cannabis Dispensary`}
                description={`Live cannabis menu for ${store?.name || "ZenZest"}. Real-time prices and inventory across flower, vapes, edibles, concentrates, pre-rolls, and accessories. NY OCM-licensed.`}
                canonical={canonical}
            />

            <MenuHeader
                storeSlug={storeSlug}
                store={store}
                menu={menu}
                visibleCount={visibleCount}
                needsClientFilter={needsClientFilter}
                subcategory={subcategory}
                category={category}
                specialsCount={classifiedSpecials.length}
                onOpenMobileFilters={() => setMobileFiltersOpen(true)}
                activeFilterCount={activeFilterCount}
                sortValue={sortValue}
                onSortChange={onSortChange}
                onClearAll={clearAll}
                categories={categories}
                onCategory={(slug) => {
                    setParam("subcategory", null);
                    setParam("category", slug || null);
                }}
                subcategories={subcategories}
                onSubcategory={(s) => setParam("subcategory", s || null)}
            />

            {/* Body — sidebar filters + product grid (full-width container) */}
            <section className="bg-zen-bg max-w-none mx-auto px-3 sm:px-5 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-5">
                    <aside className="hidden lg:block">
                        <MenuFilters {...filterPanelProps} />
                    </aside>

                    {/* Mobile filter drawer */}
                    {mobileFiltersOpen && (
                        <div
                            data-testid="menu-mobile-filters-drawer"
                            className="fixed inset-0 z-[130] bg-zen-ink/40 backdrop-blur-sm lg:hidden"
                            onClick={() => setMobileFiltersOpen(false)}
                        >
                            <aside
                                onClick={(e) => e.stopPropagation()}
                                className="absolute top-0 left-0 bottom-0 w-[85%] max-w-sm bg-zen-bg p-6 overflow-y-auto"
                            >
                                <div className="flex items-center justify-between mb-6">
                                    <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive">
                                        Filters & Sort
                                    </p>
                                    <button
                                        onClick={() => setMobileFiltersOpen(false)}
                                        aria-label="Close filters"
                                    >
                                        <X size={20} />
                                    </button>
                                </div>

                                {/* Sort selector lives inside the mobile
                                    drawer so the sticky top-of-page toolbar
                                    doesn't crowd the category chips. */}
                                <div className="mb-6">
                                    <p className="text-[10px] uppercase tracking-[0.3em] text-zen-olive mb-3">
                                        Sort
                                    </p>
                                    <select
                                        data-testid="menu-mobile-sort-select"
                                        value={sortValue}
                                        onChange={(e) => onSortChange(e.target.value)}
                                        className="w-full bg-white border border-zen-ink/15 hover:border-zen-ink/30 focus:border-zen-olive text-zen-ink text-sm px-3 py-2 focus:outline-none transition-colors"
                                    >
                                        {SORT_OPTIONS.map((o) => (
                                            <option key={`${o.key}:${o.dir}`} value={`${o.key}:${o.dir}`}>
                                                {o.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <MenuFilters {...filterPanelProps} />

                                {activeFilterCount > 0 && (
                                    <button
                                        onClick={clearAll}
                                        data-testid="menu-mobile-clear-filters"
                                        className="mt-6 w-full py-2.5 border border-zen-ink/20 text-[11px] uppercase tracking-[0.22em] text-zen-ink hover:bg-zen-surface"
                                    >
                                        Clear all filters
                                    </button>
                                )}
                            </aside>
                        </div>
                    )}

                    <div>
                        <MenuGrid
                            pageSize={PAGE_SIZE}
                            error={error}
                            menu={menu}
                            visibleProducts={visibleProducts}
                            visibleCount={visibleCount}
                            needsClientFilter={needsClientFilter}
                            pageStart={pageStart}
                            offsetParam={offsetParam}
                            hasPrev={hasPrev}
                            hasNext={hasNext}
                            onPrev={() => setOffset(Math.max(0, offsetParam - PAGE_SIZE))}
                            onNext={() => setOffset(offsetParam + PAGE_SIZE)}
                            onClearAll={clearAll}
                            storeSlug={storeSlug}
                            specialsById={specialsById}
                        />
                    </div>
                </div>
            </section>
        </>
    );
}
